import axios from 'axios';

const MISTRAL_API_KEY = process.env.REACT_APP_MISTRAL_API_KEY;
const MISTRAL_API_URL = 'https://api.mistral.ai/v1/chat/completions';

/**
 * Convert image file to base64
 * @param {File} file - Image file
 * @returns {Promise<string>} Base64 encoded image
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Extract VO2 Max table data from image using Mistral AI
 * @param {File} imageFile - Image file containing VO2 Max table
 * @returns {Promise<Array>} Array of VO2 data rows
 */
export const extractVO2DataFromImage = async (imageFile) => {
  try {
    // Convert image to base64
    const base64Image = await fileToBase64(imageFile);

    // Prepare the prompt for Mistral AI
    const prompt = `You are an expert at extracting data from medical test reports. 
    
Analyze this VO2 Max test table image and extract ALL the data rows.

The table has the following columns:
- Stage (number)
- Time (min:sec format like "1:00" or "11:15")
- HR (BPM - heart rate in beats per minute)
- Incline (Grade - decimal number)
- Speed (KPH - decimal number)
- VO2 (mL/min - decimal number)
- VO2 (mL/kg/min - decimal number)
- VE/VO2 (decimal number)
- Calories (Cals/min - decimal number)
- Calories (Cals/hr - whole number)

Extract ALL rows from the table and return ONLY a valid JSON array with this exact structure:
[
  {
    "stage": 1,
    "time": "1:00",
    "hr": "129",
    "incline": "1.0",
    "speed": "7.0",
    "vo2_ml": "1085",
    "vo2_lkg": "14.3",
    "ve_vo2": "28.35",
    "calories_min": "5.4",
    "calories_hr": "328"
  }
]

IMPORTANT:
1. Return ONLY the JSON array, no other text or explanation
2. Extract ALL rows from the table
3. Keep decimal precision as shown in the image
4. Time format should be "min:sec" (e.g., "1:00", "11:15")
5. All numeric values should be strings in the JSON
6. Do not skip any rows`;

    // Call Mistral API
    const response = await axios.post(
      MISTRAL_API_URL,
      {
        model: 'mistral-small-latest',
        messages: [
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: prompt
              },
              {
                type: 'image_url',
                image_url: base64Image
              }
            ]
          }
        ],
        temperature: 0.1,
        max_tokens: 2000
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${MISTRAL_API_KEY}`
        }
      }
    );

    // Extract the response content
    const content = response.data.choices[0].message.content;
    
    // Try to parse the JSON response
    let jsonData;
    try {
      // Remove markdown code blocks if present
      const cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      jsonData = JSON.parse(cleanedContent);
    } catch (parseError) {
      console.error('Failed to parse Mistral response:', content);
      throw new Error('Failed to parse the extracted data. Please try again or enter data manually.');
    }

    // Validate the data structure
    if (!Array.isArray(jsonData) || jsonData.length === 0) {
      throw new Error('No valid data extracted from the image. Please try again or enter data manually.');
    }

    // Validate each row has required fields
    const requiredFields = ['stage', 'time', 'hr', 'incline', 'speed', 'vo2_ml', 'vo2_lkg', 've_vo2', 'calories_min', 'calories_hr'];
    for (const row of jsonData) {
      for (const field of requiredFields) {
        if (!(field in row)) {
          throw new Error(`Missing field "${field}" in extracted data. Please check the image quality.`);
        }
      }
    }

    return jsonData;
  } catch (error) {
    console.error('Error extracting VO2 data from image:', error);
    
    if (error.response) {
      // API error
      throw new Error(`Mistral API error: ${error.response.data?.message || error.response.statusText}`);
    } else if (error.message) {
      // Custom error message
      throw error;
    } else {
      // Generic error
      throw new Error('Failed to extract data from image. Please try again or enter data manually.');
    }
  }
};
